import React from 'react';
import {Box, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';

export const getSettingsColors = theme => theme.palette.mode === 'dark' ? {
    canvas: theme.palette.background.default, card: theme.palette.background.darkBlue,
    inset: theme.palette.background.paper, input: theme.palette.background.input,
    border: theme.palette.divider, text: theme.palette.text.primary, muted: theme.palette.text.secondary
} : {
    canvas: theme.palette.background.default, card: theme.palette.background.paper,
    inset: '#eef3f8', input: '#f3f6fa', border: theme.palette.divider,
    text: theme.palette.text.primary, muted: theme.palette.text.secondary
};

export const settingsInputStyles = {
    '& .MuiOutlinedInput-root': {bgcolor: theme => getSettingsColors(theme).input, borderRadius: '8px'},
    '& .MuiOutlinedInput-input': {bgcolor: 'transparent', color: theme => getSettingsColors(theme).text,
        '&::placeholder': {color: theme => getSettingsColors(theme).muted, opacity: 1}},
    '& .MuiOutlinedInput-notchedOutline': {borderColor: theme => getSettingsColors(theme).border},
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {border: '1px solid', borderColor: 'primary.main'},
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {border: '1px solid', borderColor: 'error.main'},
    '& .MuiInputLabel-root': {color: theme => getSettingsColors(theme).muted,
        '&.Mui-focused': {color: 'primary.main'}, '&.Mui-error': {color: 'error.main'}}
};

export const settingsTabButtonStyles = {
    minHeight: 35, px: 2, py: 0.75, fontSize: 13, lineHeight: 1.75,
    borderRadius: '7px', whiteSpace: 'nowrap', textTransform: 'none'
};

export const settingsRowHeight = 55;

export const settingsTableStyles = {
    '& .MuiTableCell-head': {
        bgcolor: theme => getSettingsColors(theme).inset, color: theme => getSettingsColors(theme).muted,
        fontSize: 12, fontWeight: 600, borderLeft: 0, py: 1.5, height: 48, boxSizing: 'border-box'
    },
    '& .MuiTableCell-body': {fontSize: 13, py: 1.5, height: settingsRowHeight, boxSizing: 'border-box'},
    '& .MuiTableRow-root:last-child .MuiTableCell-body': {borderBottom: 0}
};

export const settingsTabsStyles = {
    mb: 2, overflowX: 'auto',
    '& .MuiList-root': {gap: 0.5, p: 0.5, border: 0, borderRadius: '10px', bgcolor: theme => getSettingsColors(theme).card},
    '& .MuiListItemButton-root': {
        ...settingsTabButtonStyles,
        '&.Mui-selected': {
            bgcolor: theme => alpha(theme.palette.primary.main, 0.1), color: 'primary.main',
            borderBottomWidth: 0, mb: 0
        },
        '& .MuiListItemText-root': {my: 0},
        '& .MuiTypography-root': {color: 'inherit', fontSize: 13, lineHeight: 1.75}
    }
};

// Scoped to settings content so menus, dialogs, and the sidebar retain their own styles.
export const settingsPageStyles = {
    ...settingsInputStyles,
    ...settingsTableStyles,
    bgcolor: theme => getSettingsColors(theme).canvas,
    color: theme => getSettingsColors(theme).text,
    minWidth: 0,
    px: {xs: 2, sm: 0},
    pb: 4,
    '& .MuiPaper-root:not(.MuiAlert-root)': {
        position: 'relative', minWidth: 0, bgcolor: theme => getSettingsColors(theme).card, border: '1px solid', borderColor: theme => getSettingsColors(theme).border,
        borderRadius: '12px', boxShadow: 'none', p: {xs: 2, md: 3}
    },
    '& .MuiTableContainer-root': {border: theme => theme.palette.mode === 'dark' ? 0 : 1, borderColor: theme => getSettingsColors(theme).border, borderRadius: '8px', mt: 2},
    '& .MuiButton-root': {borderRadius: '8px'},
    '& .MuiChip-root': {borderRadius: '6px'},
    '& .settings-tabs': settingsTabsStyles
};

export function SettingsPageHeader({title}) {
    return <Box sx={{mb: 3}}>
        <Typography component="h1"
                    sx={{fontSize: {xs: 24, md: 28}, fontWeight: 700, letterSpacing: '-0.5px', whiteSpace: 'normal'}}>
            {title}
        </Typography>
    </Box>;
}

export function SettingsEmptyState({icon: Icon, title = 'No records found', description}) {
    return <Box sx={{textAlign: 'center', px: 2, py: 5}}>
        <Icon sx={{fontSize: 36, color: 'text.secondary', mb: 1}}/>
        <Typography sx={{fontWeight: 600, mb: 0.5, whiteSpace: 'normal'}}>{title}</Typography>
        <Typography sx={{fontSize: 13, color: 'text.secondary', whiteSpace: 'normal', fontWeight: 400}}>
            {description}
        </Typography>
    </Box>;
}
